import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import apiClient from 'api/client';

export const billingKeys = {
  overview: ['billing-overview'],
  plans: ['billing-plans'],
  profile: ['billing-profile'],
  invoices: ['billing-invoices'],
  usage: (metric) => ['billing-usage', metric ?? 'all'],
};

export function useBillingOverview(options = {}) {
  return useQuery({
    queryKey: billingKeys.overview,
    queryFn: () => apiClient.get('/api/v1/billing/overview/').then((r) => r.data),
    ...options,
  });
}

export function usePlans() {
  return useQuery({
    queryKey: billingKeys.plans,
    queryFn: () => apiClient.get('/api/v1/billing/plans/').then((r) => r.data),
  });
}

export function useBillingProfile() {
  return useQuery({
    queryKey: billingKeys.profile,
    queryFn: () => apiClient.get('/api/v1/billing/profile/').then((r) => r.data),
  });
}

export function useUpdateBillingProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data) => apiClient.patch('/api/v1/billing/profile/', data).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: billingKeys.profile });
      qc.invalidateQueries({ queryKey: billingKeys.overview });
    },
  });
}

export function useInvoices() {
  return useQuery({
    queryKey: billingKeys.invoices,
    queryFn: () => apiClient.get('/api/v1/billing/invoices/').then((r) => r.data),
  });
}

export function useUsage(metric) {
  return useQuery({
    queryKey: billingKeys.usage(metric),
    queryFn: () =>
      apiClient
        .get('/api/v1/billing/usage/', { params: metric ? { metric } : {} })
        .then((r) => r.data),
  });
}

export function useCheckout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data) => apiClient.post('/api/v1/billing/checkout/', data).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: billingKeys.overview });
      qc.invalidateQueries({ queryKey: billingKeys.invoices });
    },
  });
}

export function useChangePlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data) =>
      apiClient.post('/api/v1/billing/subscription/change-plan/', data).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: billingKeys.overview }),
  });
}

export function useCancelSubscription() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiClient.post('/api/v1/billing/subscription/cancel/').then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: billingKeys.overview }),
  });
}

export function useRevokeCard() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => apiClient.delete(`/api/v1/billing/payment-methods/${id}/`),
    onSuccess: () => qc.invalidateQueries({ queryKey: billingKeys.overview }),
  });
}
